/**
 * ShapefileUtils.js
 * Utility functions for handling shapefiles and converting them to GeoJSON
 */

import shp from 'shpjs';
import * as turf from '@turf/turf';
import {
  Feature,
  FeatureCollection,
  Polygon,
  LineString,
  MultiPolygon,
  Point,
  Geometry,
  Position,
  GeoJsonProperties,
} from 'geojson';

// Demo AB line GeoJSON to use with the demo field boundary
export const demoABLineGeoJSON: FeatureCollection<LineString> = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: {
        Field: 'Field 1.1.3',
        Dataset: '8/12/2020 1',
        Product: 'NO Product',
        Obj__Id: 1.0,
        Guid_Detai:
          'Type=AB ; Ref Points=A: -88.443158680000,33.265696220000 | B: -88.442991995621,33.271454459911 ; Passes=1 ; AB Heading=1.387 deg ; Swath Width=120.00 ft',
        AB_Line: 'AB Heading=1.387 deg ; Swath Width=120.00 ft',
      },
      geometry: {
        type: 'LineString',
        coordinates: [
          [-88.4431587, 33.2656962],
          [-88.442992, 33.2714545],
        ],
      },
    },
  ],
};

// Demo field boundary GeoJSON
export const demoFieldBoundaryGeoJSON: FeatureCollection<Polygon> = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: {
        Field: 'Field 1.1.3',
        Dataset: '8/12/2020',
        Product: 'NO Product',
        Obj__Id: 1.0,
        Bnd_Name: '8/12/2020',
      },
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [-88.4431881, 33.2714901],
            [-88.4431905, 33.2709604],
            [-88.4432675, 33.2704758],
            [-88.4432692, 33.2700808],
            [-88.4432269, 33.2699729],
            [-88.4433355, 33.2695872],
            [-88.4433389, 33.268833],
            [-88.4433515, 33.2684021],
            [-88.4433313, 33.2681417],
            [-88.4433455, 33.2673427],
            [-88.4433682, 33.2670375],
            [-88.4433535, 33.265547],
            [-88.4433219, 33.2654482],
            [-88.443151, 33.2654117],
            [-88.4430121, 33.2654023],
            [-88.4428411, 33.2654197],
            [-88.4425844, 33.2654548],
            [-88.4422423, 33.2654717],
            [-88.4420286, 33.2654711],
            [-88.441836, 33.2655243],
            [-88.4418037, 33.2655781],
            [-88.4417926, 33.2656768],
            [-88.4416637, 33.2658111],
            [-88.4415884, 33.2659186],
            [-88.4414486, 33.2661067],
            [-88.441267, 33.2661061],
            [-88.4411049, 33.2664917],
            [-88.4409867, 33.2666349],
            [-88.4409226, 33.2666258],
            [-88.4406851, 33.2671727],
            [-88.4402229, 33.2677458],
            [-88.4396752, 33.2683367],
            [-88.4396003, 33.2683454],
            [-88.4395042, 33.2683361],
            [-88.4394081, 33.2683179],
            [-88.439333, 33.2683805],
            [-88.4392367, 33.2683981],
            [-88.4390011, 33.2685141],
            [-88.4389366, 33.2685857],
            [-88.4387548, 33.2686211],
            [-88.4385838, 33.2686205],
            [-88.4385404, 33.268755],
            [-88.4383693, 33.2687725],
            [-88.4381659, 33.2688436],
            [-88.437887, 33.2690762],
            [-88.4378863, 33.2692378],
            [-88.4377579, 33.2692553],
            [-88.437587, 33.2692458],
            [-88.4375652, 33.2693355],
            [-88.4375435, 33.2694073],
            [-88.4374793, 33.2694161],
            [-88.4372122, 33.2693973],
            [-88.4370736, 33.269325],
            [-88.4369133, 33.2693155],
            [-88.4367848, 33.2693869],
            [-88.4367308, 33.2695035],
            [-88.4368158, 33.2696025],
            [-88.4368047, 33.2697012],
            [-88.4367193, 33.269683],
            [-88.4366976, 33.2697547],
            [-88.4366122, 33.2697275],
            [-88.4365587, 33.2697453],
            [-88.4365582, 33.2698531],
            [-88.4366539, 33.2699521],
            [-88.4365893, 33.2700686],
            [-88.4365567, 33.2701763],
            [-88.4365774, 33.2703379],
            [-88.4367694, 33.2704104],
            [-88.4369404, 33.2704109],
            [-88.437506, 33.2706103],
            [-88.4377836, 33.270674],
            [-88.4382742, 33.270909],
            [-88.4389047, 33.2709379],
            [-88.4394385, 33.2710743],
            [-88.4400257, 33.2712108],
            [-88.4407949, 33.2712761],
            [-88.441436, 33.271332],
            [-88.4424828, 33.2714789],
            [-88.4431875, 33.2716248],
            [-88.4431879, 33.271544],
            [-88.4431881, 33.2714901],
          ],
        ],
      },
    },
  ],
};

/**
 * Converts a shapefile to GeoJSON format
 * @param {File|ArrayBuffer} shpFile - The .shp file object or buffer
 * @param {File|ArrayBuffer} dbfFile - The .dbf file object or buffer
 * @param {File|ArrayBuffer|null} cpgFile - The .cpg file object or buffer (optional, for character encoding)
 * @param {File|ArrayBuffer|string|null} prjFile - The .prj file object or buffer or string (optional, for projection)
 * @returns {Promise<Object>} - GeoJSON object
 */
export const convertShapefileToGeoJSON = async (
  shpFile: File | ArrayBuffer,
  dbfFile: File | ArrayBuffer | null,
  cpgFile: File | ArrayBuffer | null = null,
  prjFile: File | ArrayBuffer | string | null = null,
): Promise<FeatureCollection> => {
  try {
    // Convert Files to ArrayBuffers if needed
    const shpBuffer: ArrayBuffer =
      shpFile instanceof ArrayBuffer
        ? shpFile
        : await (shpFile as File).arrayBuffer();
    const dbfBuffer: ArrayBuffer | null =
      dbfFile instanceof ArrayBuffer
        ? dbfFile
        : dbfFile
        ? await (dbfFile as File).arrayBuffer()
        : null;
    const cpgBuffer: ArrayBuffer | null =
      cpgFile instanceof ArrayBuffer
        ? cpgFile
        : cpgFile
        ? await (cpgFile as File).arrayBuffer()
        : null;

    // Handle PRJ content
    let prjContent: string | null = null;
    if (prjFile) {
      if (typeof prjFile === 'string') {
        prjContent = prjFile;
      } else {
        const prjBuffer =
          prjFile instanceof ArrayBuffer
            ? prjFile
            : await (prjFile as File).arrayBuffer();
        // Convert ArrayBuffer to string - ISO-8859-1 is the most common CPG we see
        const decoder = new TextDecoder('ISO-8859-1');
        prjContent = decoder.decode(prjBuffer);
      }
    }

    // Parse shapefile using prj information if available
    const geometries: Geometry[] = await shp.parseShp(
      shpBuffer,
      prjContent || undefined,
    );

    // If we have a DBF, parse the attributes
    let attributes: GeoJsonProperties[] = [];
    if (dbfBuffer) {
      // Second parameter should be a buffer - create an empty one if CPG is not available
      const emptyBuffer = new ArrayBuffer(0);
      attributes = await shp.parseDbf(dbfBuffer, cpgBuffer || emptyBuffer);
    }

    // Create the GeoJSON feature collection
    const geojson: FeatureCollection = {
      type: 'FeatureCollection',
      features: geometries.map((geometry, i) => ({
        type: 'Feature',
        properties: attributes[i] || {},
        geometry: geometry,
      })),
    };

    return geojson;
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to convert shapefile: ${message}`);
  }
};

/**
 * Processes a zip file containing shapefile components
 * @param {File} zipFile - The .zip file object
 * @returns {Promise<Object>} - GeoJSON object
 */
export const processZipFile = async (
  zipFile: File,
): Promise<FeatureCollection> => {
  try {
    // Convert Zip File to ArrayBuffer
    const zipBuffer = await zipFile.arrayBuffer();

    // Use shp.js to parse the zip file directly into GeoJSON
    const geojsonResult = await shp(zipBuffer);

    // Sometimes shp.js returns an array of GeoJSON objects if the ZIP contains
    // multiple shapefiles. In that case, use the first one.
    const geojson: FeatureCollection = Array.isArray(geojsonResult)
      ? geojsonResult[0]
      : geojsonResult;

    return geojson;
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to process zip file: ${message}`);
  }
};

/**
 * Extracts files from FileList into a map by extension
 * @param {FileList|File[]} files - FileList or array of files
 * @returns {Object} - Map of file extensions to File objects
 */
export const extractShapefileComponents = (
  files: FileList | File[],
): { [key: string]: File } => {
  const fileMap: { [key: string]: File } = {};

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext) {
      fileMap[ext] = file;
    }
  }

  // Validate minimum required files
  if (!fileMap.shp || !fileMap.dbf) {
    throw new Error('Minimum shapefile components required: .shp and .dbf');
  }

  return fileMap;
};

/**
 * Creates a GeoJSON object for an AB line
 * @param {Array} linePoints - Array of [lng, lat] coordinates for the line
 * @returns {Object} - GeoJSON line object
 */
export const createABLineGeoJSON = (
  linePoints: Position[],
): FeatureCollection<LineString> => {
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: linePoints,
        },
      },
    ],
  };
};

/**
 * Parse a shapefile set (multiple files) and return GeoJSON
 * @param {FileList|File[]} files - FileList or array of files
 * @returns {Promise<Object>} - GeoJSON object
 */
export const parseShapefileSet = async (
  files: FileList | File[],
): Promise<FeatureCollection> => {
  try {
    // Check if we have a zip file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (ext === 'zip') {
        return await processZipFile(file);
      }
    }

    // Otherwise process individual shapefile components
    const fileMap = extractShapefileComponents(files);
    // Check if we have optional files and pass them if available
    return await convertShapefileToGeoJSON(
      fileMap.shp!,
      fileMap.dbf!,
      fileMap.cpg || null,
      fileMap.prj || null,
    );
  } catch (error: unknown) {
    throw error;
  }
};

/**
 * Calculates the Minimum Rotated Rectangle (MRR) for a polygon
 * This is an implementation of the rotating calipers algorithm since Turf
 * doesn't have a built-in function for this and that's what we do on the backend
 * @param {Object} polygon - GeoJSON polygon
 * @returns {Object} - Object containing UTM corners, angle, width and height of MRR
 */
// Define a type for the MRR result for clarity
type MRRResult = {
  corners: Position[];
  angle: number;
  width: number;
  height: number;
  utmPolygon: Feature<Polygon>;
};

export const calculateMRR = (polygon: Feature<Polygon>): MRRResult => {
  // Convert polygon to UTM coordinates for accurate calculations
  const utmPolygon = turf.toMercator(polygon) as Feature<Polygon>;

  // Create the convex hull of the polygon in UTM coordinates
  const hull = turf.convex(utmPolygon);

  // Get hull coordinates and remove the last point (duplicate of first)
  if (
    !hull ||
    !hull.geometry ||
    !hull.geometry.coordinates ||
    hull.geometry.coordinates.length === 0
  ) {
    throw new Error('Failed to compute convex hull or hull is empty');
  }
  const hullPoints = hull.geometry.coordinates[0].slice(0, -1);

  // Implementation of rotating calipers algorithm for minimum area rectangle
  let minArea = Infinity;
  let bestRect: MRRResult | null = null;

  // For each edge of the convex hull
  for (let i = 0; i < hullPoints.length; i++) {
    const p1 = hullPoints[i];
    const p2 = hullPoints[(i + 1) % hullPoints.length];

    // Calculate edge direction vector
    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];

    // Calculate angle of the edge with respect to x-axis
    const angle = Math.atan2(dy, dx);

    // Calculate sin and cos of the angle
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);

    // Rotate all points to align with axes
    const rotatedPoints = hullPoints.map((p) => {
      // Translate p to origin from p1
      const x = p[0] - p1[0];
      const y = p[1] - p1[1];

      // Rotate
      const xRot = x * cosA + y * sinA;
      const yRot = -x * sinA + y * cosA;

      return [xRot, yRot];
    });

    // Find min/max x and y of rotated points
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    for (const p of rotatedPoints) {
      minX = Math.min(minX, p[0]);
      minY = Math.min(minY, p[1]);
      maxX = Math.max(maxX, p[0]);
      maxY = Math.max(maxY, p[1]);
    }

    // Compute area of the rectangle
    const width = maxX - minX;
    const height = maxY - minY;
    const area = width * height;

    if (area < minArea) {
      minArea = area;

      // Convert corners back to original UTM coordinates
      const utmCorners = [
        [minX, minY], // Bottom left
        [maxX, minY], // Bottom right
        [maxX, maxY], // Top right
        [minX, maxY], // Top left
      ].map((p) => {
        // Rotate back
        const x = p[0] * cosA - p[1] * sinA;
        const y = p[0] * sinA + p[1] * cosA;

        // Translate back
        return [x + p1[0], y + p1[1]];
      });

      bestRect = {
        corners: utmCorners,
        angle: angle,
        width,
        height,
        utmPolygon,
      };
    }
  }

  if (!bestRect) {
    throw new Error('Failed to compute minimum rotated rectangle');
  }

  return bestRect;
};

/**
 * Guesses the optimal AB line heading based on field boundary geometry. This should
 * nearly mirror the behavior of the science library's guess_abline_heading function.
 * Since that is what is used to actually create the trial, what matters for this function
 * is that it be visually as close as possible for the user.
 * @param {Object} fieldBoundaryGeoJSON - GeoJSON object of the field boundary
 * @param {number} [index=0] - Index of the preferred side (0 for longest)
 * @returns {number} - Heading in degrees (0-360)
 */
export const guessABLineHeading = (
  fieldBoundaryGeoJSON: FeatureCollection<Polygon | MultiPolygon>,
  index: number = 0,
): number => {
  try {
    if (
      !fieldBoundaryGeoJSON ||
      !fieldBoundaryGeoJSON.features ||
      fieldBoundaryGeoJSON.features.length === 0
    ) {
      throw new Error('Invalid field boundary GeoJSON');
    }

    // Extract the field boundary polygon
    const feature = fieldBoundaryGeoJSON.features[0];
    if (!feature || !feature.geometry) {
      throw new Error('Invalid feature or geometry in field boundary');
    }
    if (
      feature.geometry.type !== 'Polygon' &&
      feature.geometry.type !== 'MultiPolygon'
    ) {
      throw new Error('Field boundary must be a polygon or multipolygon');
    }

    // Get the coordinates of the polygon
    let coordinates: Position[];
    if (feature.geometry.type === 'Polygon') {
      coordinates = feature.geometry.coordinates[0]; // Outer ring
    } else {
      // Take the largest polygon from MultiPolygon
      const areas = feature.geometry.coordinates.map((polyCoords) => {
        const polygon = turf.polygon([polyCoords[0]]); // Assume outer ring of each multi-polygon part
        return { poly: polyCoords[0], area: turf.area(polygon) };
      });
      areas.sort((a, b) => b.area - a.area);
      coordinates = areas[0].poly;
    }

    // Create a polygon from the coordinates
    const polygon = turf.polygon([coordinates]);

    // Calculate the Minimum Rotated Rectangle in UTM coordinates
    const bestRect = calculateMRR(polygon);

    // Create sides from the rectangle corners in UTM
    const utmCorners = bestRect.corners;
    const sides: {
      startUTM: Position;
      endUTM: Position;
      length: number;
      angle: number;
      normalizedHeadingQuadrant: number;
      cardinal: string;
      cardinalPriority: number;
      nsAlignment: number;
    }[] = [];

    for (let i = 0; i < utmCorners.length; i++) {
      const nextIdx = (i + 1) % utmCorners.length;

      const start = utmCorners[i];
      const end = utmCorners[nextIdx];

      // Calculate length
      const length = Math.sqrt(
        Math.pow(end[0] - start[0], 2) + Math.pow(end[1] - start[1], 2),
      );

      // Calculate direction vector and angle
      const dx = end[0] - start[0];
      const dy = end[1] - start[1];
      const angle = (Math.atan2(dy, dx) * 180) / Math.PI; // convert to degrees
      const normalizedAngle = (angle + 360) % 360;

      // Calculate normalized heading in 0-90 quadrant for alignment
      const normalizedHeadingQuadrant = normalizedAngle % 90;

      // Calculate how close the heading is to north-south (0° or 90°)
      const nsAlignment = Math.min(
        normalizedHeadingQuadrant,
        90 - normalizedHeadingQuadrant,
      );

      // Explicitly classify the heading into a cardinal direction
      // This matches the Python version's cardinal direction logic
      let cardinal: string;
      if (
        (0 <= normalizedAngle && normalizedAngle < 45) ||
        (315 <= normalizedAngle && normalizedAngle <= 360)
      ) {
        cardinal = 'N'; // North
      } else if (45 <= normalizedAngle && normalizedAngle < 135) {
        cardinal = 'E'; // East
      } else if (135 <= normalizedAngle && normalizedAngle < 225) {
        cardinal = 'S'; // South
      } else {
        cardinal = 'W'; // West
      }

      // Preference order: N > S > E > W
      const cardinalPriority = { N: 0, S: 1, E: 2, W: 3 }[cardinal] as number;

      sides.push({
        startUTM: start,
        endUTM: end,
        length,
        angle: normalizedAngle,
        normalizedHeadingQuadrant,
        cardinal,
        cardinalPriority,
        nsAlignment,
      });
    }

    // First sort by length (longest first)
    sides.sort((a, b) => b.length - a.length);

    // Check if we have a square or rectangle with similar sides
    const longestLength = sides[0].length;
    const similarSides: typeof sides = [];

    // Collect all sides that are within 2% of the longest side
    for (const side of sides) {
      const lengthRatio = side.length / longestLength;
      if (lengthRatio > 0.98) {
        similarSides.push(side);
      }
    }

    // If we have multiple similar sides (rectangle or square)
    if (similarSides.length >= 2) {
      // Check if all sides have extremely close NS alignments (within 0.001)
      const allAlignments = similarSides.map((side) => side.nsAlignment);
      const minAlignment = Math.min(...allAlignments);
      const verySimilarAlignments = allAlignments.every(
        (a) => Math.abs(a - minAlignment) < 0.001,
      );

      if (verySimilarAlignments) {
        // Sort by cardinal priority (N-S over E-W), then by absolute heading (smaller is better)
        similarSides.sort((a, b) => {
          if (a.cardinalPriority !== b.cardinalPriority) {
            return a.cardinalPriority - b.cardinalPriority;
          }
          // For equal cardinal priorities, prefer smaller absolute heading
          const aHeading = a.angle <= 180 ? a.angle : a.angle - 360;
          const bHeading = b.angle <= 180 ? b.angle : b.angle - 360;
          return aHeading - bHeading;
        });
      } else {
        // Sort by NS alignment (lower is better), then by heading (smaller is better)
        similarSides.sort((a, b) => {
          if (a.nsAlignment !== b.nsAlignment) {
            return a.nsAlignment - b.nsAlignment;
          }
          // For equal NS alignments, prefer smaller absolute heading
          const aHeading = a.angle <= 180 ? a.angle : a.angle - 360;
          const bHeading = b.angle <= 180 ? b.angle : b.angle - 360;
          return aHeading - bHeading;
        });
      }

      // Replace the sorted sides at the beginning of our sides array
      similarSides.forEach((side, i) => {
        sides[i] = side;
      });
    }

    // Select the side based on index (default 0 will be longest or most north-south aligned)
    const selectedSide = sides[index % sides.length];

    // Convert the selected side from UTM back to WGS84 to calculate the final heading
    const utmLine = turf.lineString([
      selectedSide.startUTM,
      selectedSide.endUTM,
    ]);
    const wgs84Line = turf.toWgs84(utmLine) as Feature<LineString>; // Assume toWgs84 preserves type
    const wgs84Coords = wgs84Line.geometry.coordinates;

    // Calculate the bearing (heading) in WGS84
    const heading = turf.bearing(
      turf.point(wgs84Coords[0]),
      turf.point(wgs84Coords[1]),
    );

    // Convert to 0-360 range
    const normalizedHeading = (heading + 360) % 360;

    return normalizedHeading;
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to calculate AB line heading: ${message}`);
  }
};

/**
 * Creates an AB line from a heading
 * @param {number} heading - Heading in degrees (0-360)
 * @param {Object} fieldBoundaryGeoJSON - GeoJSON of the field boundary for positioning
 * @returns {Array} - Array of [lng, lat] coordinates for the AB line
 */
export const createABLineFromHeading = (
  heading: number,
  fieldBoundaryGeoJSON: FeatureCollection<Polygon | MultiPolygon>,
): Position[] => {
  try {
    if (
      !fieldBoundaryGeoJSON ||
      !fieldBoundaryGeoJSON.features ||
      fieldBoundaryGeoJSON.features.length === 0
    ) {
      throw new Error('Invalid field boundary GeoJSON');
    }

    // Find the center of the field boundary
    const center = turf.center(fieldBoundaryGeoJSON);
    if (!center || !center.geometry) {
      throw new Error('Failed to calculate center of field boundary');
    }
    const centerPoint = center.geometry.coordinates;

    // Calculate the bounding box to determine line length
    const bbox = turf.bbox(fieldBoundaryGeoJSON);
    const width = turf.distance(
      turf.point([bbox[0], bbox[1]]),
      turf.point([bbox[2], bbox[1]]),
    );
    const height = turf.distance(
      turf.point([bbox[0], bbox[1]]),
      turf.point([bbox[0], bbox[3]]),
    );

    // Make the line long enough to cross the entire field
    const lineLength = Math.max(width, height) * 1.5;

    // Use the heading directly as bearing (0 = north, then clockwise)
    const bearing = heading;

    // Calculate the start and end points for the line
    const startPoint = turf.destination(
      centerPoint,
      lineLength / 2,
      bearing + 180,
    );
    const endPoint = turf.destination(centerPoint, lineLength / 2, bearing);

    // Return the line coordinates
    return [startPoint.geometry.coordinates, endPoint.geometry.coordinates];
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to create AB line: ${message}`);
  }
};

/**
 * Rotates an AB line by a specified angle
 * @param {Array} lineCoordinates - Array of [lng, lat] coordinates for the line
 * @param {number} rotationDegrees - Rotation angle in degrees (positive = clockwise)
 * @returns {Array} - Array of [lng, lat] coordinates for the rotated line
 */
export const rotateABLine = (
  lineCoordinates: Position[],
  rotationDegrees: number,
): Position[] => {
  try {
    if (!lineCoordinates || lineCoordinates.length < 2) {
      throw new Error('Invalid AB line coordinates');
    }

    // Normalize rotation angle to 0-360 degrees
    const normalizedRotation = ((rotationDegrees % 360) + 360) % 360;

    // Create a line from the coordinates
    const line = turf.lineString(lineCoordinates);

    // Find the center point of the line to rotate around
    const center = turf.center(line);
    if (!center || !center.geometry) {
      throw new Error('Failed to calculate center of line');
    }

    // Rotate each point of the line around the center
    const rotatedCoordinates = lineCoordinates.map((coord) => {
      // Create a point from the coordinate
      const point = turf.point(coord);

      // Rotate the point around the center using the normalized rotation
      const rotated = turf.transformRotate(point, normalizedRotation, {
        pivot: center.geometry.coordinates,
      }) as Feature<Point>; // Assume transformRotate preserves type

      // Return the rotated coordinates
      return rotated.geometry.coordinates;
    });

    return rotatedCoordinates;
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to rotate AB line: ${message}`);
  }
};

/**
 * Translates (shifts) an AB line by a specified distance
 * @param {Array} lineCoordinates - Array of [lng, lat] coordinates for the line
 * @param {number} distanceFeet - Distance to shift in feet (positive = right, negative = left)
 * @returns {Array} - Array of [lng, lat] coordinates for the translated line
 */
export const translateABLine = (
  lineCoordinates: Position[],
  distanceFeet: number,
): Position[] => {
  try {
    if (!lineCoordinates || lineCoordinates.length < 2) {
      throw new Error('Invalid AB line coordinates');
    }

    // Convert feet to meters for UTM calculations
    const distanceMeters = distanceFeet * 0.3048; // 1 foot = 0.3048 meters

    // Create a line string from the coordinates
    const line = turf.lineString(lineCoordinates);

    // Convert to UTM for accurate distance calculations
    const utmLine = turf.toMercator(line) as Feature<LineString>; // Assume toMercator preserves type
    const utmCoords = utmLine.geometry.coordinates;

    // Calculate the bearing in UTM
    const dx = utmCoords[1][0] - utmCoords[0][0];
    const dy = utmCoords[1][1] - utmCoords[0][1];

    // Calculate perpendicular vector (90 degrees to the right)
    // To rotate 90 degrees clockwise: (dx, dy) -> (dy, -dx)
    const perpDx = dy;
    const perpDy = -dx;

    // Normalize the perpendicular vector
    const length = Math.sqrt(perpDx * perpDx + perpDy * perpDy);
    const normPerpDx = perpDx / length;
    const normPerpDy = perpDy / length;

    // Apply the shift to each point in UTM
    const translatedUtmCoords = utmCoords.map((coord) => [
      coord[0] + normPerpDx * distanceMeters,
      coord[1] + normPerpDy * distanceMeters,
    ]);

    // Convert back to WGS84
    const translatedUtmLine = turf.lineString(translatedUtmCoords);
    const translatedWgs84Line = turf.toWgs84(
      translatedUtmLine,
    ) as Feature<LineString>; // Assume toWgs84 preserves type

    return translatedWgs84Line.geometry.coordinates;
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to translate AB line: ${message}`);
  }
};

/**
 * Adjust an AB line by rotating and/or translating it
 * @param {Array} lineCoordinates - Array of [lng, lat] coordinates for the line
 * @param {number} rotationDegrees - Rotation angle in degrees (positive = clockwise)
 * @param {number} translationFeet - Distance to shift in feet (positive = right, negative = left)
 * @returns {Array} - Array of [lng, lat] coordinates for the adjusted line
 */
export const adjustABLine = (
  lineCoordinates: Position[],
  rotationDegrees: number,
  translationFeet: number,
): Position[] => {
  try {
    if (!lineCoordinates || lineCoordinates.length < 2) {
      throw new Error('Invalid AB line coordinates');
    }

    // First rotate the line if needed
    let adjustedCoordinates = lineCoordinates;
    if (rotationDegrees !== 0) {
      adjustedCoordinates = rotateABLine(adjustedCoordinates, rotationDegrees);
    }

    // Then translate the line if needed
    if (translationFeet !== 0) {
      adjustedCoordinates = translateABLine(
        adjustedCoordinates,
        translationFeet,
      );
    }

    return adjustedCoordinates;
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to adjust AB line: ${message}`);
  }
};
